<script setup lang="ts">
import { ref, computed } from 'vue'
import FbBanner from '@/components/ui/FbBanner.vue'
import { useServiceWorker } from '@/composables/useServiceWorker'
import { createLogger } from '@/utils/logger'
import { captureError } from '@/utils/sentry'

const logger = createLogger('UpdatePrompt')
const { needRefresh, updateServiceWorker } = useServiceWorker()
const dismissed = ref(false)
const isUpdating = ref(false)
const updateFailed = ref(false)

const showPrompt = computed(() => needRefresh.value && !dismissed.value)

async function handleUpdate() {
  isUpdating.value = true
  updateFailed.value = false
  try {
    await updateServiceWorker()
  } catch (error) {
    logger.error('Update failed:', error)
    captureError(error instanceof Error ? error : new Error('Service worker update failed'), {
      source: 'service-worker',
      event: 'update-failed'
    })
    updateFailed.value = true
    isUpdating.value = false
  }
  // Note: Page will reload on success, so no need to reset isUpdating
}

function handleLater() {
  dismissed.value = true
}
</script>

<template>
  <FbBanner v-if="showPrompt" tone="update" icon="mdi-update">
    <span>A fresh version of Fam-Bam is ready.</span>
    <v-alert
      v-if="updateFailed"
      type="error"
      variant="tonal"
      class="mt-2"
      data-testid="update-error-alert"
    >
      Update failed — please refresh the page
    </v-alert>
    <template #actions>
      <v-btn variant="text" color="primary" :disabled="isUpdating" data-testid="later-btn" @click="handleLater">Later</v-btn>
      <v-btn color="primary" variant="flat" :loading="isUpdating" :disabled="isUpdating" data-testid="update-now-btn" @click="handleUpdate">Reload</v-btn>
    </template>
  </FbBanner>
</template>
